import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TextInput from 'components/Common/Form/Input/TextInput'
import IconButton from 'LuxKit/Button/IconButton'
import SolidMessageIcon from 'LuxKit/Icons/solid/SolidMessageIcon'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { selectCurrentChat, selectOutageMessage } from 'supportAssistant/selectors/selectCurrentChat'
import { connect } from 'react-redux'

interface MappedStateProps {
  currentChat: App.Chat | undefined
  outageMessage: string | undefined
}
interface Props {
  isTyping: boolean
}

function MessageInput(props: Props & MappedStateProps) {
  const { currentChat, outageMessage, isTyping } = props
  const intl = useIntl()

  const dispatch = useAppDispatch()

  const messages: Array<App.ChatMessage> = useAppSelector(state => state.supportAssistant.messages[currentChat?.id ?? 'default'] ?? EmptyArray)
  const [message, setMessage] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  const isMobile = useScreenSizeOnly('mobile')

  const lastMessage = last(messages)
  const isLastMessageByUser = lastMessage?.sender === 'user'
  const lastMessageMetaInputAction = lastMessage?.meta?.content?.find((meta) => meta.type === 'input')

  const validateInput = useCallback((msg: string, actionValidation?: { regex: string, message: string }) => {
    if (!actionValidation) {
      return msg.length < 250 ? '' : intl.formatMessage({
        id: 'UpcCSfioPO',
        defaultMessage: 'Messages are limited to 250 characters',
        description: 'Validation error when chat message exceeds character limit',
      })
    }

    return new RegExp(actionValidation.regex).test(msg) ? '' : actionValidation.message
  }, [intl])

  const validationMessage = validateInput(message, lastMessageMetaInputAction?.validation)
  const isMessageValid = validationMessage === ''

  const awaitingInitialAgentConnection = useMemo(() =>
    currentChat?.agentType === 'AGENT' &&
    (!messages.some((msg) => msg.text === 'You are now speaking to an agent') &&
      !messages.some((msg) => msg.sender === 'agent')
    ), [currentChat, messages])

  const isInputButtonDisabled = !lastMessageMetaInputAction && (currentChat?.status === 'CLOSED' || !message || isTyping || !!outageMessage || (currentChat?.agentType === 'BOT' && isLastMessageByUser)) || !isMessageValid
  const isInputDisabled = currentChat?.status === 'CLOSED' || awaitingInitialAgentConnection

  useEffect(() => {
    if (!message && !isMobile) {
      inputRef.current?.focus({
        preventScroll: true,
      })
    }
  }, [isMobile, message])

  const onMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(event.target.value)
  }

  const handleMessageSubmit = useCallback(() => {
    // we need to attach the action of the last message to the payload.
    // This is so the back end understands the action to be done when free text is entered.
    const messageMeta: App.ChatMessageMeta = lastMessageMetaInputAction ?
        {
          action: lastMessageMetaInputAction.action?.key,
          payload: { ...lastMessage?.meta?.payload, inputPayload: message },
        } : {
          payload: lastMessage?.meta?.payload,
        }

    dispatch(addChatMessage(message, messageMeta))
    setMessage('')
  }, [dispatch, lastMessage?.meta?.payload, lastMessageMetaInputAction, message])

  const onKeyEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isEnter = event.type === 'keyup' && event.key === 'Enter'

    if (isEnter && !isInputButtonDisabled) {
      handleMessageSubmit()
    }
  }

  const onMessageSubmit = () => {
    handleMessageSubmit()
  }

  const agentConnectionPlaceholder = intl.formatMessage({
    id: 'VYj6NB/B2x',
    defaultMessage: 'We are connecting you with an agent...',
    description: 'Placeholder text shown while waiting for agent connection',
  })

  const placeholder = lastMessageMetaInputAction ? lastMessageMetaInputAction?.text : intl.formatMessage({
    id: 'azKfnODKDd',
    defaultMessage: 'Type a message',
    description: 'Default placeholder text for chat message input',
  })

  return (
    <TextInput
      type="text"
      maxLength={250}
      disabled={isInputDisabled}
      name="Message"
      aria-label={intl.formatMessage({
        id: 'lVk/0j1zsA',
        defaultMessage: 'Message',
        description: 'Aria label for chat message input field',
      })}
      autoComplete="off"
      placeholder={awaitingInitialAgentConnection ? agentConnectionPlaceholder : placeholder}
      onChange={onMessageChange}
      value={message}
      ref={inputRef}
      onKeyUp={onKeyEnter}
      invalidErrorMessage={outageMessage}
      manualError
      noValidationSpacing
      helpText={validationMessage}
      endIcon={<IconButton
        kind={lastMessageMetaInputAction ? 'primary' : 'tertiary'}
        variant="dark"
        shape="circle"
        horizontalOutdent="end"
        onClick={onMessageSubmit}
        disabled={isInputButtonDisabled}
      >
        <SolidMessageIcon />
      </IconButton>}
    />
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    currentChat: selectCurrentChat(state),
    outageMessage: selectOutageMessage(state),
  }
})(MessageInput)
